import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Tooltip,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

interface Subtopic {
  name: string;
  keywords: string[];
}

interface Topic {
  name: string;
  subtopics: Subtopic[];
}

interface TopicsData {
  topics: Topic[];
}

interface TopicsVisualizationProps {
  data: TopicsData;
}

const TopicsVisualization: React.FC<TopicsVisualizationProps> = ({ data }) => {
  // Track which topics are expanded (all collapsed by default)
  const [expandedTopics, setExpandedTopics] = useState<Set<number>>(
    new Set()
  );

  const toggleTopic = (index: number) => {
    setExpandedTopics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Identified Topics & Subtopics
          </Typography>
          <Chip 
            label={`${data.topics.length} Topic${data.topics.length !== 1 ? 's' : ''}`}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ 
              fontWeight: 600
            }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          The analysis identified the following topics and subtopics based on your keywords.
        </Typography>
      </Box>

      {/* Topics Cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {data.topics.map((topic, topicIndex) => {
          const isExpanded = expandedTopics.has(topicIndex);
          
          return (
            <Card key={topicIndex}>
              {/* Clickable Topic Header */}
              <Box
                onClick={() => toggleTopic(topicIndex)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {/* Expand/Collapse Icon */}
                <IconButton
                  size="small"
                  sx={{ 
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    }
                  }}
                >
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                
                {/* Topic Title */}
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    flex: 1,
                  }}
                >
                  {topic.name}
                </Typography>
                
                {/* Subtopic Count Chip */}
                <Chip 
                  label={`${topic.subtopics.length} subtopic${topic.subtopics.length !== 1 ? 's' : ''}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ 
                    fontWeight: 500
                  }}
                />
              </Box>

              {/* Divider with padding */}
              {isExpanded && (
                <Box 
                  sx={{ 
                    mx: 2, 
                    borderBottom: '2px solid',
                    borderColor: 'primary.main',
                  }} 
                />
              )}

              {/* Collapsible Subtopics Content */}
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <CardContent sx={{ pt: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {topic.subtopics.map((subtopic, subtopicIndex) => (
                    <Box 
                      key={subtopicIndex}
                      sx={{
                        pl: 2,
                        borderLeft: '4px solid',
                        borderColor: 'primary.light',
                      }}
                    >
                      {/* Subtopic Name */}
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 600,
                          mb: 1,
                          color: 'text.primary'
                        }}
                      >
                        {subtopic.name}
                      </Typography>

                      {/* Keywords Label */}
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: 'block',
                          mb: 1,
                          color: 'text.secondary',
                          fontWeight: 500
                        }}
                      >
                        Keywords:
                      </Typography>

                      {/* Keywords Chips */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {subtopic.keywords.map((keyword, keywordIndex) => {
                          const isLongText = keyword.length > 50;
                          const chip = (
                            <Chip
                              label={keyword}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: '0.8rem',
                                height: '24px',
                                maxWidth: '480px',
                                borderColor: 'divider',
                                '& .MuiChip-label': {
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'block',
                                },
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                },
                              }}
                            />
                          );
                          
                          return isLongText ? (
                            <Tooltip key={keywordIndex} title={keyword} arrow>
                              {chip}
                            </Tooltip>
                          ) : (
                            <Box key={keywordIndex}>{chip}</Box>
                          );
                        })}
                      </Box>
                    </Box>
                  ))}
                  </Box>
                </CardContent>
              </Collapse>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default TopicsVisualization;

